import type { Resolution, TimePeriod } from '@carboncopyco/types';
import {
  type DropdownFilterItemContent,
  Separator,
  TextBase,
  TextLargeParagraph,
} from '@carboncopyco/ui';
import Highcharts from 'highcharts';
import { HighchartsReact } from 'highcharts-react-official';
import HC_rounded from 'highcharts-rounded-corners';
import type { ColumnChartConfigType } from '../../types';
import { getResolutionOptions } from '../../utils';
import { EmptyState } from '../EmptyState';
import { ActivityChartFilterBar } from './ActivityChartFilterBar';
import { ActivityChartSkeleton } from './ActivityChartSkeleton';
import { getBaseConfig } from './getBaseConfig';

HC_rounded(Highcharts);

type ActivityChartProps = {
  title?: string;
  isLoading: boolean;
  isError: boolean;
  isEmpty: boolean;
  chartData: ColumnChartConfigType | [];
  resolution: Resolution;
  setResolution: (resolution: Resolution) => void;
  chartHeight?: number;
  trackEvent?: () => void;
  timePeriod: TimePeriod;
  timePeriodOptions?: DropdownFilterItemContent<TimePeriod>[];
  setTimePeriod?: (period: TimePeriod) => void;
};

export const SingleBarChart = ({
  title = 'Activity over time',
  timePeriod,
  isLoading,
  isError,
  isEmpty,
  chartData,
  resolution,
  setResolution,
  chartHeight,
  trackEvent,
  setTimePeriod,
  timePeriodOptions,
}: ActivityChartProps) => {
  return (
    <div className="flex flex-col w-full h-full bg-ds-accent3-100 rounded-lg border border-ds-natural-150">
      <div className="flex justify-between items-center h-ds-15 px-6 shrink-0">
        <TextLargeParagraph
          label={title}
          color="text-ds-natural-700"
        />
        <ActivityChartFilterBar
          setResolution={(resolutionItem) => {
            trackEvent?.();
            setResolution(resolutionItem);
          }}
          resolution={resolution}
          resolutionOptions={getResolutionOptions(timePeriod)}
          setTimePeriod={
            setTimePeriod ? (period) => setTimePeriod(period) : undefined
          }
          timePeriod={timePeriod}
          timePeriodOptions={timePeriodOptions}
        />
      </div>
      <div className="w-full">
        <Separator horizontal />
      </div>
      <div className="grow mx-5 mt-5 mb-3">
        {isLoading ? (
          <div className="flex justify-center items-center h-full">
            <ActivityChartSkeleton />
          </div>
        ) : isError ? (
          <div className="h-full flex items-center justify-center">
            <div className="flex flex-col items-center">
              <TextBase
                label="Oops! something went wrong"
                color="text-ds-natural-700"
              />
              <TextBase
                label="We are looking into it, please try again later"
                color="text-ds-natural-400"
              />
            </div>
          </div>
        ) : (
          <div className="h-full">
            {isEmpty ? (
              <div className="h-full flex justify-center items-center">
                <EmptyState />
              </div>
            ) : (
              <div className="w-full">
                <HighchartsReact
                  highcharts={Highcharts}
                  options={getBaseConfig({
                    ...chartData,
                    chartHeight,
                  })}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
